const SESSION_EVENT_REFRESH_DEBOUNCE_MS = 5_000;

type SessionEventRefreshCoordinatorOptions = {
  active: boolean;
  refresh: (isCurrent: () => boolean, immediate: boolean) => Promise<void>;
};

/** Canonical bounded event refresh policy shared by session-list owners. */
export function createSessionEventRefreshCoordinator({
  active: initialActive,
  refresh,
}: SessionEventRefreshCoordinatorOptions) {
  let active = initialActive;
  let timer: ReturnType<typeof setTimeout> | 0 = 0;
  let nextAllowed = 0;
  let pending: object | null = null;
  let revision = 0;
  // Hidden pages and in-flight requests retain one trailing invalidation.
  let queued = false;
  let immediate = false;
  let retryAt: number | null = null;
  let fallback: ReturnType<typeof setTimeout> | undefined;

  const clearTimer = () => {
    clearTimeout(timer);
    timer = 0;
  };

  const arm = (debounce = true) => {
    if (!active || pending || !queued || timer) {
      return;
    }
    const now = Date.now();
    const delay = debounce ? SESSION_EVENT_REFRESH_DEBOUNCE_MS * (1 - 0.2 * Math.random()) : 0;
    timer = setTimeout(
      start,
      retryAt !== null
        ? Math.max(0, retryAt - now)
        : immediate
          ? 0
          : Math.max(delay, nextAllowed - now),
    );
  };

  const start = () => {
    clearTimer();
    if (!active || pending || !queued) {
      return;
    }
    queued = false;
    const requestImmediate = immediate;
    immediate = false;
    retryAt = null;
    const request = {};
    pending = request;
    const started = Date.now();
    const requestRevision = revision;
    void refresh(() => pending === request && requestRevision === revision, requestImmediate)
      .catch(() => {})
      .finally(() => {
        if (pending !== request) {
          return;
        }
        pending = null;
        const completed = Date.now();
        nextAllowed = completed + Math.min(15_000, Math.max(5_000, 3 * (completed - started)));
        arm();
      });
  };

  const absorb = () => {
    clearTimeout(fallback);
    fallback = undefined;
    revision += 1;
    clearTimer();
    queued = false;
    immediate = false;
    retryAt = null;
  };
  const reset = () => {
    absorb();
    pending = null;
    nextAllowed = 0;
  };

  return {
    scheduleFallback() {
      fallback ??= setTimeout(() => {
        fallback = undefined;
        queued = true;
        arm();
      }, 60_000);
    },
    scheduleRetry(delayMs: number) {
      retryAt = Date.now() + delayMs;
      queued = true;
      clearTimer();
      arm(false);
    },
    schedule(expedite = false) {
      queued = true;
      if (expedite && !immediate) {
        // Creation skips collection/backoff, not active-request or visibility ownership.
        immediate = true;
        clearTimer();
      }
      arm();
    },
    setActive(next: boolean, markDirty = false) {
      active = next;
      if (next) {
        arm(false);
        return;
      }
      queued ||= markDirty || timer !== 0;
      clearTimer();
    },
    absorb,
    reset,
    dispose: reset,
  };
}
